import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { SensorGroupSourceTag, SensorGroupTypeTag } from './property/SensorGroupTags';
import type { MeterSensorGroup, MeterSensorGroupProperty } from '../hooks/useMeterSensorGroups';

function HierarchyLabel({
  parentId,
  parentName,
  childId,
  childName,
}: {
  parentId: number | null;
  parentName: string | null;
  childId: number | null;
  childName: string | null;
}) {
  if (parentId) {
    return <span className='text-sm'>Child of {parentName}</span>;
  }
  if (childId) {
    return <span className='text-sm'>Master &rarr; {childName}</span>;
  }
  return <span className='text-sm text-muted-foreground'>Standalone</span>;
}

export function MeterSensorGroupsCard({
  sensorGroups,
  isLoading,
}: {
  sensorGroups: MeterSensorGroup[];
  isLoading: boolean;
}) {
  if (isLoading) return null;

  return (
    <Card>
      <CardHeader className='pb-3'>
        <CardTitle className='text-lg'>Sensor Groups ({sensorGroups.length})</CardTitle>
      </CardHeader>
      <CardContent>
        {sensorGroups.length === 0
          ? (
            <p className='text-sm text-muted-foreground'>
              No sensor groups found for this sensor
            </p>
          )
          : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Metron ID</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Property</TableHead>
                  <TableHead>Hierarchy</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sensorGroups.map((sg) => (
                  <TableRow key={sg.sensorGroupId}>
                    <TableCell>{sg.sensorGroupName}</TableCell>
                    <TableCell className='font-mono'>
                      {sg.sensorGroupMetronId ?? <span>&mdash;</span>}
                    </TableCell>
                    <TableCell>
                      <SensorGroupSourceTag source={sg.source} />
                    </TableCell>
                    <TableCell>
                      <SensorGroupTypeTag type={sg.waterscopeType} />
                    </TableCell>
                    <TableCell>
                      {sg.propertyName ?? <span className='text-muted-foreground'>&mdash;</span>}
                    </TableCell>
                    <TableCell>
                      <HierarchyLabel
                        parentId={sg.parentSensorGroupId}
                        parentName={sg.parentSensorGroupName}
                        childId={sg.childSensorGroupId}
                        childName={sg.childSensorGroupName}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
      </CardContent>
    </Card>
  );
}

export function MeterPropertiesCard({
  properties,
  isLoading,
}: {
  properties: MeterSensorGroupProperty[];
  isLoading: boolean;
}) {
  if (isLoading) return null;

  return (
    <Card>
      <CardHeader className='pb-3'>
        <CardTitle className='text-lg'>Properties ({properties.length})</CardTitle>
      </CardHeader>
      <CardContent>
        {properties.length === 0
          ? (
            <p className='text-sm text-muted-foreground'>
              No property associations found for this sensor
            </p>
          )
          : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Property</TableHead>
                  <TableHead>Metron ID</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Sensor Group</TableHead>
                  <TableHead>Hierarchy</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {properties.map((prop) => (
                  <TableRow key={`${prop.propertyId}-${prop.sensorGroupId}`}>
                    <TableCell>{prop.propertyName}</TableCell>
                    <TableCell className='font-mono'>
                      {prop.propertyMetronId ?? <span>&mdash;</span>}
                    </TableCell>
                    <TableCell>
                      {prop.customerName ?? <span className='text-muted-foreground'>&mdash;</span>}
                    </TableCell>
                    <TableCell>{prop.sensorGroupName}</TableCell>
                    <TableCell>
                      {prop.masterSensorGroupId
                        ? <span className='text-sm'>Master: {prop.masterSensorGroupName}</span>
                        : <span className='text-sm text-muted-foreground'>Standalone</span>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
      </CardContent>
    </Card>
  );
}
